/**
 * 立项调研自定义Hook
 * 处理竞品列表、痛点列表的状态管理，以及数据加载/保存
 */

import { useState, useEffect, useCallback } from 'react';
import { FormInstance, App } from 'antd';
import axios from 'axios';

interface UseProjectResearchProps {
    skuId?: number;
    form: FormInstance;
}

interface UseProjectResearchReturn {
    competitors: string[];
    painPoints: string[];
    addCompetitor: () => void;
    removeCompetitor: (index: number) => void;
    updateCompetitor: (index: number, value: string) => void;
    addPainPoint: () => void;
    removePainPoint: (index: number) => void;
    updatePainPoint: (index: number, value: string) => void;
    handleSave: () => Promise<void>;
}

/**
 * 通用的列表项操作Hook
 */
const useListManager = (initialItems: string[] = ['']) => {
    const [items, setItems] = useState<string[]>(initialItems);

    const addItem = useCallback(() => {
        setItems(prev => [...prev, '']);
    }, []);

    const removeItem = useCallback((index: number) => {
        setItems(prev => {
            const newItems = prev.filter((_, i) => i !== index);
            return newItems.length > 0 ? newItems : [''];
        });
    }, []);

    const updateItem = useCallback((index: number, value: string) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[index] = value;
            return newItems;
        });
    }, []);

    const setAllItems = useCallback((newItems: string[]) => {
        setItems(newItems.length > 0 ? newItems : ['']);
    }, []);

    return {
        items,
        addItem,
        removeItem,
        updateItem,
        setAllItems,
    };
};

export const useProjectResearch = ({
    skuId,
    form,
}: UseProjectResearchProps): UseProjectResearchReturn => {
    const { message } = App.useApp();

    const {
        items: competitors,
        addItem: addCompetitor,
        removeItem: removeCompetitor,
        updateItem: updateCompetitor,
        setAllItems: setCompetitors,
    } = useListManager(['']);

    const {
        items: painPoints,
        addItem: addPainPoint,
        removeItem: removePainPoint,
        updateItem: updatePainPoint,
        setAllItems: setPainPoints,
    } = useListManager(['']);

    // 加载调研数据
    const loadResearchData = useCallback(async () => {
        if (!skuId) return;

        try {
            const response = await axios.get(`/api/skus/${skuId}/`);
            const research = response.data.research_data || {};

            // 设置竞品列表
            if (research.competitors && Array.isArray(research.competitors)) {
                setCompetitors(research.competitors);
            }

            // 设置痛点列表
            if (research.pain_points && Array.isArray(research.pain_points)) {
                setPainPoints(research.pain_points);
            }
        } catch (error) {
            console.error('加载调研数据失败:', error);
        }
    }, [skuId, setCompetitors, setPainPoints]);

    // 保存调研数据
    const handleSave = useCallback(async () => {
        try {
            const values = await form.validateFields();

            // 整合调研数据
            const researchData = {
                ...values,
                competitors: competitors.filter(c => c.trim()),
                pain_points: painPoints.filter(p => p.trim()),
            };

            if (skuId) {
                await axios.patch(`/api/skus/${skuId}/`, {
                    research_data: researchData
                });
                message.success('调研数据已保存');
            }
        } catch (error) {
            console.error('保存失败:', error);
            message.error('保存失败');
        }
    }, [form, competitors, painPoints, skuId, message]);

    // 初始化加载
    useEffect(() => {
        if (skuId) {
            loadResearchData();
        }
    }, [skuId, loadResearchData]);

    return {
        competitors,
        painPoints,
        addCompetitor,
        removeCompetitor,
        updateCompetitor,
        addPainPoint,
        removePainPoint,
        updatePainPoint,
        handleSave,
    };
};

export default useProjectResearch;
